import { createContext, useContext } from 'react';

/**
 * One quote drawer for the whole app, so any grid can offer a quote link without its host
 * page wiring up a drawer of its own.
 *
 * The need came from the Quote # column: it is a FIELD DEFINITION, so it can be added to
 * Search Results, an LC dashboard, a pro grid — anywhere the field picker reaches. A cell
 * cannot mount a drawer and a column generator has no host to ask, so the opener lives
 * here and `generateColumnDef` only has to call it.
 *
 * THE CONTEXT ONLY. The provider that mounts the real drawer is a separate module
 * (QuoteDrawerProvider) on purpose: dynamicColumns is a util imported by node-environment
 * tests and by every grid, and importing it from here dragged QuoteLaneEditorDrawer — and
 * through it the MSAL-bound api client — into all of them.
 *
 * `available` is false when no provider is mounted. That is a real state, not a defect:
 * a quote number that renders as plain text there is correct, and nothing throws.
 */
export interface QuoteDrawerValue {
  /** Open a quote revision in the shared drawer. */
  openQuote: (quoteNumber: number, revisionNumber: number) => void;
  /** Whether a provider is mounted — cells render plain text when it is not. */
  available: boolean;
}

const QuoteDrawerContext = createContext<QuoteDrawerValue>({
  openQuote: () => {},
  available: false,
});

export const useQuoteDrawer = (): QuoteDrawerValue => useContext(QuoteDrawerContext);

export default QuoteDrawerContext;
